import { createFileRoute } from "@tanstack/react-router";
/**
 * Suborder Review Page
 *
 * This page allows users to review the details of an order and add notes.
 *
 * The suborderId param is required.
 */
import { useSuspenseQuery } from "@tanstack/react-query";
import { suspenseOptions } from "@/lib/suspense-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { getGetSuborderQueryOptions } from "@/requests/gen/react-query/suborder";
import OrderContentsTable from "@/components/universal/order-contents-table";
import SuborderDetailsCard from "./-suborder-details-card";
import SuborderConfirmCard from "./-suborder-confirm-card";
import SuborderHistory from "./-suborder-history";
import { Header } from "@/components/universal/header";
import SuborderNoteForm from "./-suborder-note-form";
import { SuborderLabels } from "./-suborder-labels";
import SuborderCancelRefundActions from "./-suborder-cancel-refund/suborder-cancel-refund-actions";

export const Route = createFileRoute(
  "/_private/_pharmacy-360/suborder/$suborderId/",
)({
  loader: ({ context: { queryClient }, params: { suborderId } }) =>
    queryClient.ensureQueryData(getGetSuborderQueryOptions(suborderId)),
  component: SuborderReviewPage,
});

function SuborderReviewPage() {
  const { suborderId } = Route.useParams();
  // Fetch the order data
  const suborder = useSuspenseQuery(
    suspenseOptions(getGetSuborderQueryOptions(suborderId)),
  );

  const isCancelled = suborder.data?.status === "Cancelled";

  return (
    <div>
      <title>{`Review Suborder ${suborderId} - Legrande Health Portal`}</title>
      <Header
        crumbs={[
          { name: "Suborders", link: { to: "/suborder/lookup" } },
          {
            name: "Review",
            link: {
              to: "/suborder/$suborderId",
              params: { suborderId },
            },
          },
        ]}
      >
        {suborder.data && (
          <SuborderCancelRefundActions
            suborder={suborder.data}
            refetch={suborder.refetch}
          />
        )}
      </Header>
      <main className="grid flex-1 items-start gap-4 xl:grid-cols-3">
        {/* Big Column */}
        <div className="grid gap-4 xl:col-span-2">
          {/* Contents Section */}
          <div className="relative">
            <Card>
              <CardHeader>
                <CardTitle>Contents</CardTitle>
                <CardDescription>
                  Review the suborder contents below.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <h3 className="font-semibold">
                  {suborder.data?.fulfilling_pharmacy.name}
                </h3>

                {suborder.data && (
                  <OrderContentsTable
                    orderContents={suborder.data.suborder_products}
                    suborderStatus={suborder.data.status}
                    refetch={suborder.refetch}
                    suborderId={suborder.data.suborder_id}
                    isPractice={false}
                    canSplit={
                      suborder.data.status === "Draft" ||
                      suborder.data.status === "Open"
                    }
                    fulfillingPharmacyId={suborder.data.fulfilling_pharmacy_id}
                  />
                )}
                <SuborderLabels
                  refetch={suborder.refetch}
                  suborder={suborder.data}
                  canCreateLabels={
                    suborder.data?.status !== "Draft" &&
                    !!suborder.data?.charged_at
                  }
                />
              </CardContent>
            </Card>
            {isCancelled && (
              <div className="absolute inset-0 bg-order-cancelled z-50 rounded-lg" />
            )}
          </div>

          {/* History Section */}
          <Card className="gap-2">
            <CardHeader>
              <CardTitle className="text-base">History</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <SuborderHistory suborder={suborder.data} />
              <SuborderNoteForm
                suborder={suborder.data}
                onSuccess={suborder.refetch}
              />
            </CardContent>
          </Card>
        </div>

        {/* Small Column */}
        <div className="grid gap-4">
          <div className="relative">
            {/* Details Section */}
            <SuborderDetailsCard suborder={suborder.data} />
            {isCancelled && (
              <div className="absolute inset-0 bg-order-cancelled z-50 rounded-lg" />
            )}
          </div>

          <div className="relative">
            {/* Confirm Section */}
            {suborder.data && (
              <SuborderConfirmCard
                suborder={suborder.data}
                refetch={suborder.refetch}
              />
            )}
            {isCancelled && (
              <div className="absolute inset-0 bg-order-cancelled z-50 rounded-lg" />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
